import React from 'react';
import { ClockIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const PlaybookHero = ({ 
  image, 
  bannerUrl, 
  title, 
  subtitle,
  category, 
  date, 
  readingTime, 
  type, 
  icon, 
  background, 
  children 
}) => {
  const bgImage = bannerUrl || image;
  
  return (
    <motion.div 
      className="relative rounded-xl overflow-hidden shadow-2xl mb-10"
      initial="hidden"
      animate="visible"
      variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
      transition={{ duration: 0.5 }}
    >
      {bgImage && (
        <img 
          src={bgImage} 
          alt={title || 'Hero image'} 
          className="w-full h-[260px] sm:h-[340px] md:h-[420px] object-cover object-center absolute inset-0" 
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/90 via-gray-900/70 to-transparent" />
      <div className="relative p-6 sm:p-10 flex flex-col justify-end h-[260px] sm:h-[340px] md:h-[420px]">
        {/* Title comes first for better visual hierarchy */}
        {title && (
          <h1 className="inline-block bg-gray-900/70 text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-5 drop-shadow-lg px-5 py-3 rounded-lg backdrop-blur-sm">
            {title}
          </h1>
        )}
        
        {/* Single row for book icon, category, read time, and tags - now below title */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* Book icon + Category */}
          {category && (
            <div className="flex items-center bg-primary/20 rounded-full px-3 py-1.5">
              <BookOpenIcon className="h-4 w-4 text-primary-light mr-1.5" />
              <span className="text-primary-light text-xs font-semibold uppercase">
                {category}
              </span>
            </div>
          )}
          
          {/* Reading time */}
          {readingTime && (
            <div className="flex items-center bg-gray-800/40 rounded-full px-3 py-1.5">
              <ClockIcon className="h-4 w-4 text-gray-300 mr-1.5" />
              <span className="text-xs text-gray-300">{readingTime}</span>
            </div>
          )}
          
          {/* Tags */}
          {type && Array.isArray(type) && type.map(tag => (
            <span key={tag} className="inline-block bg-gray-800/40 text-primary-light text-xs font-semibold rounded-full px-3 py-1.5">
              #{tag}
            </span>
          ))}
          
          {/* Single tag case */}
          {type && !Array.isArray(type) && (
            <span className="inline-block bg-gray-800/40 text-primary-light text-xs font-semibold rounded-full px-3 py-1.5">
              #{type}
            </span>
          )}
          
          {/* Date if provided */}
          {date && (
            <span className="inline-block bg-gray-800/40 text-gray-300 text-xs rounded-full px-3 py-1.5">{date}</span>
          )}
        </div>
        
        {/* Optional icon passed from parent */}
        {icon && (
          <div className="mb-3">
            {icon}
          </div>
        )}
        
        {children}
      </div>
    </motion.div>
  );
};

export default PlaybookHero; 